import {
  DEFAULT_QUERY_PARAMS,
  QUERY_TYPES,
  formatQueryStatValue,
  getStatAbbrev,
} from "./queryBuilder";

export const QUERY_PRESET_CATEGORIES = [
  { id: "all", label: "All" },
  { id: "milestones", label: "Milestones" },
  { id: "power", label: "Power" },
  { id: "contact", label: "Contact" },
  { id: "production", label: "Production" },
  { id: "rate", label: "Rate stats" },
  { id: "seasons", label: "Single season" },
  { id: "players", label: "Player traits" },
  { id: "teams", label: "Teams" },
];

const careerPreset = ({
  id,
  title,
  subtitle,
  category,
  featured = false,
  tags = [],
  stats,
  filters,
  sortBy,
  sortDirection = -1,
  extraParams = {},
}) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  tags,
  params: {
    ...DEFAULT_QUERY_PARAMS,
    queryType: "career",
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection,
    ...extraParams,
  },
});

const seasonPreset = ({
  id,
  title,
  subtitle,
  category,
  featured = false,
  tags = [],
  stats,
  filters,
  sortBy,
  sortDirection = -1,
  startYear = 1900,
  endYear = 2025,
  extraParams = {},
}) => ({
  id,
  title,
  subtitle,
  category,
  featured,
  tags,
  params: {
    ...DEFAULT_QUERY_PARAMS,
    queryType: "season",
    startYear,
    endYear,
    selectedStats: stats,
    statisticalFilters: filters,
    sortBy,
    sortDirection,
    ...extraParams,
  },
});

export const QUERY_PRESETS = [
  careerPreset({
    id: "500-hr",
    title: "500+ HR Club",
    subtitle: "Career sluggers who reached 500 home runs",
    category: "milestones",
    featured: true,
    tags: ["legend", "power"],
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 500 } },
    sortBy: "homeruns",
  }),
  careerPreset({
    id: "600-hr",
    title: "600+ HR",
    subtitle: "The rarest elite power tier",
    category: "milestones",
    tags: ["legend"],
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 600 } },
    sortBy: "homeruns",
  }),
  careerPreset({
    id: "3000-hits",
    title: "3,000+ Hits",
    subtitle: "Career contact milestones",
    category: "milestones",
    featured: true,
    tags: ["legend", "contact"],
    stats: ["hits"],
    filters: { hits: { operator: ">=", value: 3000 } },
    sortBy: "hits",
  }),
  careerPreset({
    id: "2000-rbi",
    title: "2,000+ RBI",
    subtitle: "All-time run producers",
    category: "milestones",
    featured: true,
    tags: ["legend", "production"],
    stats: ["runBattedIn"],
    filters: { runBattedIn: { operator: ">=", value: 2000 } },
    sortBy: "runBattedIn",
  }),
  careerPreset({
    id: "500-doubles",
    title: "500+ Doubles",
    subtitle: "Gap power and longevity",
    category: "milestones",
    tags: ["contact"],
    stats: ["doubles"],
    filters: { doubles: { operator: ">=", value: 500 } },
    sortBy: "doubles",
  }),
  careerPreset({
    id: "300-avg-qualified",
    title: ".300+ Career AVG",
    subtitle: "Minimum 3,000 at-bats",
    category: "milestones",
    tags: ["rate"],
    stats: ["battingAverage", "atBat"],
    filters: {
      battingAverage: { operator: ">=", value: 0.3 },
      atBat: { operator: ">=", value: 3000 },
    },
    sortBy: "battingAverage",
  }),
  careerPreset({
    id: "400-hr",
    title: "400+ HR",
    subtitle: "Modern power benchmark",
    category: "power",
    tags: ["power"],
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 400 } },
    sortBy: "homeruns",
  }),
  careerPreset({
    id: "350-hr",
    title: "350+ HR",
    subtitle: "Strong career power hitters",
    category: "power",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 350 } },
    sortBy: "homeruns",
  }),
  careerPreset({
    id: "250-hr",
    title: "250+ HR",
    subtitle: "Solid long-ball careers",
    category: "power",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 250 } },
    sortBy: "homeruns",
  }),
  careerPreset({
    id: "550-slg",
    title: ".550+ SLG",
    subtitle: "Career slugging (3,000+ AB)",
    category: "power",
    tags: ["rate"],
    stats: ["sluggingAverage", "atBat"],
    filters: {
      sluggingAverage: { operator: ">=", value: 0.55 },
      atBat: { operator: ">=", value: 3000 },
    },
    sortBy: "sluggingAverage",
  }),
  careerPreset({
    id: "500-slg",
    title: ".500+ SLG",
    subtitle: "Plus power profiles",
    category: "power",
    stats: ["sluggingAverage", "atBat"],
    filters: {
      sluggingAverage: { operator: ">=", value: 0.5 },
      atBat: { operator: ">=", value: 3000 },
    },
    sortBy: "sluggingAverage",
  }),
  careerPreset({
    id: "2500-hits",
    title: "2,500+ Hits",
    subtitle: "High-volume contact hitters",
    category: "contact",
    stats: ["hits"],
    filters: { hits: { operator: ">=", value: 2500 } },
    sortBy: "hits",
  }),
  careerPreset({
    id: "2000-hits",
    title: "2,000+ Hits",
    subtitle: "Durable everyday hitters",
    category: "contact",
    stats: ["hits"],
    filters: { hits: { operator: ">=", value: 2000 } },
    sortBy: "hits",
  }),
  careerPreset({
    id: "400-doubles",
    title: "400+ Doubles",
    subtitle: "Doubles machines",
    category: "contact",
    stats: ["doubles"],
    filters: { doubles: { operator: ">=", value: 400 } },
    sortBy: "doubles",
  }),
  careerPreset({
    id: "100-triples",
    title: "100+ Triples",
    subtitle: "Speed and extra-base threes",
    category: "contact",
    stats: ["triples"],
    filters: { triples: { operator: ">=", value: 100 } },
    sortBy: "triples",
  }),
  careerPreset({
    id: "1500-rbi",
    title: "1,500+ RBI",
    subtitle: "Runners driven in",
    category: "production",
    stats: ["runBattedIn"],
    filters: { runBattedIn: { operator: ">=", value: 1500 } },
    sortBy: "runBattedIn",
  }),
  careerPreset({
    id: "1500-runs",
    title: "1,500+ Runs",
    subtitle: "Table-setters and sluggers who scored",
    category: "production",
    stats: ["runs"],
    filters: { runs: { operator: ">=", value: 1500 } },
    sortBy: "runs",
  }),
  careerPreset({
    id: "1000-walks",
    title: "1,000+ Walks",
    subtitle: "Patient hitters",
    category: "production",
    stats: ["baseOnBalls"],
    filters: { baseOnBalls: { operator: ">=", value: 1000 } },
    sortBy: "baseOnBalls",
  }),
  careerPreset({
    id: "2000-strikeouts",
    title: "2,000+ Strikeouts",
    subtitle: "Free swingers and longevity",
    category: "production",
    stats: ["strikeOut"],
    filters: { strikeOut: { operator: ">=", value: 2000 } },
    sortBy: "strikeOut",
  }),
  careerPreset({
    id: "400-obp",
    title: ".400+ OBP",
    subtitle: "Elite on-base skill (3,000+ AB)",
    category: "rate",
    stats: ["onBasePercentage", "atBat"],
    filters: {
      onBasePercentage: { operator: ">=", value: 0.4 },
      atBat: { operator: ">=", value: 3000 },
    },
    sortBy: "onBasePercentage",
  }),
  careerPreset({
    id: "370-obp",
    title: ".370+ OBP",
    subtitle: "Strong plate discipline careers",
    category: "rate",
    stats: ["onBasePercentage", "atBat"],
    filters: {
      onBasePercentage: { operator: ">=", value: 0.37 },
      atBat: { operator: ">=", value: 3000 },
    },
    sortBy: "onBasePercentage",
  }),
  careerPreset({
    id: "320-avg",
    title: ".320+ AVG",
    subtitle: "High-average careers (3,000+ AB)",
    category: "rate",
    stats: ["battingAverage", "atBat"],
    filters: {
      battingAverage: { operator: ">=", value: 0.32 },
      atBat: { operator: ">=", value: 3000 },
    },
    sortBy: "battingAverage",
  }),
  seasonPreset({
    id: "60-hr-season",
    title: "60+ HR Season",
    subtitle: "Historic single-season power",
    category: "seasons",
    tags: ["power"],
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 60 } },
    sortBy: "homeruns",
  }),
  seasonPreset({
    id: "50-hr-season",
    title: "50+ HR Season",
    subtitle: "Elite slugging seasons",
    category: "seasons",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 50 } },
    sortBy: "homeruns",
  }),
  seasonPreset({
    id: "40-hr-season",
    title: "40+ HR Season",
    subtitle: "Breakout power years",
    category: "seasons",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 40 } },
    sortBy: "homeruns",
  }),
  seasonPreset({
    id: "250-hits-season",
    title: "250+ Hits Season",
    subtitle: "Pace for 200-hit seasons and beyond",
    category: "seasons",
    stats: ["hits"],
    filters: { hits: { operator: ">=", value: 250 } },
    sortBy: "hits",
  }),
  seasonPreset({
    id: "200-hits-season",
    title: "200+ Hits Season",
    subtitle: "Classic benchmark contact seasons",
    category: "seasons",
    stats: ["hits"],
    filters: { hits: { operator: ">=", value: 200 } },
    sortBy: "hits",
  }),
  seasonPreset({
    id: "150-rbi-season",
    title: "150+ RBI Season",
    subtitle: "MVP-caliber run production",
    category: "seasons",
    stats: ["runBattedIn"],
    filters: { runBattedIn: { operator: ">=", value: 150 } },
    sortBy: "runBattedIn",
  }),
  seasonPreset({
    id: "130-runs-season",
    title: "130+ Runs Season",
    subtitle: "Table-setters and leadoff stars",
    category: "seasons",
    stats: ["runs"],
    filters: { runs: { operator: ">=", value: 130 } },
    sortBy: "runs",
  }),
  seasonPreset({
    id: "370-avg-season",
    title: ".370+ AVG Season",
    subtitle: "Batting title territory",
    category: "seasons",
    stats: ["battingAverage"],
    filters: { battingAverage: { operator: ">=", value: 0.37 } },
    sortBy: "battingAverage",
  }),
  careerPreset({
    id: "lefty-sluggers",
    title: "Lefty Sluggers",
    subtitle: "Left-handed batters with 200+ HR",
    category: "players",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 200 } },
    sortBy: "homeruns",
    extraParams: {
      biographicalFilters: { bats: "Left" },
    },
  }),
  careerPreset({
    id: "switch-power",
    title: "Switch-Hit Power",
    subtitle: "Switch hitters with 300+ HR",
    category: "players",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 300 } },
    sortBy: "homeruns",
    extraParams: {
      biographicalFilters: { bats: "Both" },
    },
  }),
  careerPreset({
    id: "yankees-sluggers",
    title: "Yankees Sluggers",
    subtitle: "200+ career HR with Yankees tenure",
    category: "teams",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 200 } },
    sortBy: "homeruns",
    extraParams: {
      teams: ["Yankees"],
    },
  }),
  careerPreset({
    id: "dodgers-contact",
    title: "Dodgers Hit Machines",
    subtitle: "1,500+ hits with Dodgers tenure",
    category: "teams",
    stats: ["hits"],
    filters: { hits: { operator: ">=", value: 1500 } },
    sortBy: "hits",
    extraParams: {
      teams: ["Dodgers"],
    },
  }),
  careerPreset({
    id: "athletics-power",
    title: "Athletics Power",
    subtitle: "150+ HR with Athletics tenure",
    category: "teams",
    stats: ["homeruns"],
    filters: { homeruns: { operator: ">=", value: 150 } },
    sortBy: "homeruns",
    extraParams: {
      teams: ["Athletics"],
    },
  }),
];

export const FEATURED_QUERY_PRESETS = QUERY_PRESETS.filter((preset) => preset.featured);

export const getPresetCategoryLabel = (categoryId) =>
  QUERY_PRESET_CATEGORIES.find((category) => category.id === categoryId)?.label ||
  categoryId;

export const getPresetPreviewChips = (preset) => {
  const chips = [];
  const { params } = preset;

  if (params.queryType !== "career") {
    const type = QUERY_TYPES.find((item) => item.value === params.queryType);
    chips.push(type?.label || params.queryType);
  }

  if (
    params.startYear !== DEFAULT_QUERY_PARAMS.startYear ||
    params.endYear !== DEFAULT_QUERY_PARAMS.endYear
  ) {
    chips.push(`${params.startYear}–${params.endYear}`);
  }

  Object.entries(params.statisticalFilters || {}).forEach(([stat, filter]) => {
    if (filter?.value == null || filter.value === "") return;
    const op = filter.operator === ">=" ? "≥" : filter.operator === "<=" ? "≤" : "=";
    chips.push(`${getStatAbbrev(stat)} ${op} ${formatQueryStatValue(stat, filter.value)}`);
  });

  if (params.biographicalFilters?.bats) {
    chips.push(`Bats ${params.biographicalFilters.bats}`);
  }
  if (params.biographicalFilters?.throws) {
    chips.push(`Throws ${params.biographicalFilters.throws}`);
  }
  if (params.biographicalFilters?.birthPlace) {
    chips.push(params.biographicalFilters.birthPlace);
  }

  (params.teams || []).forEach((team) => chips.push(team));

  return chips.slice(0, 4);
};

export const filterPresets = (presets, { category = "all", search = "" } = {}) => {
  const term = search.trim().toLowerCase();

  return presets.filter((preset) => {
    const matchesCategory = category === "all" || preset.category === category;
    if (!matchesCategory) return false;
    if (!term) return true;

    const haystack = [
      preset.title,
      preset.subtitle,
      preset.category,
      ...(preset.tags || []),
      ...getPresetPreviewChips(preset),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(term);
  });
};
